import { useState } from "react";
import { useForm } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";

import type { GetChecklistDto } from "~/data/history/get-checklists.dto";

import { getScenarios } from "~/data/scenario/scenario.service";

import { Field } from "../shared/field";
import { Button } from "../shared/button";
import { Dialog } from "../shared/dialog";
import { Input, InputSearch } from "../shared/input";

interface FiltersProps {
  onFilter: (data: GetChecklistDto) => void;
}

export function Filters({ onFilter }: FiltersProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const form = useForm<GetChecklistDto>({
    defaultValues: Object.fromEntries(searchParams),
  });
  const { data: scenarios } = useQuery({
    queryKey: ["scenarios"],
    queryFn: () => getScenarios(),
  });

  const [search, setSearch] = useState("");

  return (
    <form
      onSubmit={form.handleSubmit((data) => {
        onFilter(data);

        const entries = Object.entries(data).filter((it) => !!it[1]) as [
          string,
          string
        ][];
        setSearchParams(entries);
      })}
    >
      <Dialog.Title asChild>
        <h3 className="text-xl font-semibold mb-4">Filtro</h3>
      </Dialog.Title>
      <Dialog.Description className="sr-only">
        Filtre por período, notas e checklists
      </Dialog.Description>

      {/* Conteúdo do Modal em duas colunas */}
      <div className="grid grid-cols-2 gap-4">
        {/* Coluna 1: Período e Resultado */}
        <div>
          {/* Período */}
          <div className="mb-4 border border-gray-200 rounded-lg p-4">
            <Field label="Período" className="mb-3">
              <Input type="date" {...form.register("period_start")} />
            </Field>
            <Field label="Até">
              <Input type="date" {...form.register("period_end")} />
            </Field>
          </div>

          {/* Resultado */}
          <div className="mb-4 border border-gray-200 rounded-lg p-4">
            <label className="block mb-1 text-sm font-alternative">
              Resultado
            </label>

            {/* Filtro "ACIMA DE" */}
            <div className="flex items-center py-[10px] px-4 mb-3 bg-[#f4f4f4] rounded gap-[10px] justify-between">
              <div className="flex items-center text-white">
                <div className="bg-alternative-1 py-1 px-2 rounded">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M10 1.81818C8.92555 1.81818 7.86162 2.02981 6.86895 2.44099C5.87629 2.85216 4.97433 3.45483 4.21458 4.21458C3.45483 4.97433 2.85216 5.87629 2.44099 6.86895C2.02981 7.86162 1.81818 8.92555 1.81818 10C1.81818 11.0745 2.02981 12.1384 2.44099 13.131C2.85216 14.1237 3.45483 15.0257 4.21458 15.7854C4.97433 16.5452 5.87629 17.1478 6.86895 17.559C7.86162 17.9702 8.92555 18.1818 10 18.1818C12.17 18.1818 14.251 17.3198 15.7854 15.7854C17.3198 14.251 18.1818 12.17 18.1818 10C18.1818 7.83005 17.3198 5.74897 15.7854 4.21458C14.251 2.68019 12.17 1.81818 10 1.81818ZM0 10C0 4.47727 4.47727 0 10 0C15.5227 0 20 4.47727 20 10C20 15.5227 15.5227 20 10 20C4.47727 20 0 15.5227 0 10ZM7.27273 6.36364V10H5.45455V6.36364H7.27273ZM14.5455 6.36364V10H12.7273V6.36364H14.5455ZM5.45455 10.9091H14.5455V11.8182C14.5455 13.0237 14.0666 14.1799 13.2141 15.0323C12.3617 15.8847 11.2055 16.3636 10 16.3636C8.79447 16.3636 7.63832 15.8847 6.78588 15.0323C5.93344 14.1799 5.45455 13.0237 5.45455 11.8182V10.9091ZM7.42727 12.7273C7.61509 13.2596 7.9634 13.7205 8.42418 14.0466C8.88496 14.3726 9.43553 14.5477 10 14.5477C10.5645 14.5477 11.115 14.3726 11.5758 14.0466C12.0366 13.7205 12.3849 13.2596 12.5727 12.7273H7.42727Z"
                      fill="#FBFBFB"
                    />
                  </svg>
                </div>
              </div>
              <label
                htmlFor="acimaDe"
                className="text-sm font-alternative text-gray-700"
              >
                Acima de
              </label>
              <input
                type="number"
                placeholder="Valor"
                className="bg-[#D3D3D3] rounded px-4 h-7 w-20 max-w-full"
                {...form.register("min_score")}
              />
            </div>

            {/* Filtro "ABAIXO DE" */}
            <div className="flex items-center mb-3 py-[10px] px-4 bg-[#f4f4f4] rounded gap-[10px] justify-between">
              <div className="flex items-center text-white">
                <div className="bg-red-500 py-1 px-2 rounded">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M10 1.81818C8.92555 1.81818 7.86162 2.02981 6.86895 2.44099C5.87629 2.85216 4.97433 3.45483 4.21458 4.21458C3.45483 4.97433 2.85216 5.87629 2.44099 6.86895C2.02981 7.86162 1.81818 8.92555 1.81818 10C1.81818 11.0745 2.02981 12.1384 2.44099 13.131C2.85216 14.1237 3.45483 15.0257 4.21458 15.7854C4.97433 16.5452 5.87629 17.1478 6.86895 17.559C7.86162 17.9702 8.92555 18.1818 10 18.1818C12.17 18.1818 14.251 17.3198 15.7854 15.7854C17.3198 14.251 18.1818 12.17 18.1818 10C18.1818 7.83005 17.3198 5.74897 15.7854 4.21458C14.251 2.68019 12.17 1.81818 10 1.81818ZM0 10C0 4.47727 4.47727 0 10 0C15.5227 0 20 4.47727 20 10C20 15.5227 15.5227 20 10 20C4.47727 20 0 15.5227 0 10ZM7.27273 6.36364V10H5.45455V6.36364H7.27273ZM14.5455 6.36364V10H12.7273V6.36364H14.5455ZM5.45455 10.9091H14.5455V11.8182C14.5455 13.0237 14.0666 14.1799 13.2141 15.0323C12.3617 15.8847 11.2055 16.3636 10 16.3636C8.79447 16.3636 7.63832 15.8847 6.78588 15.0323C5.93344 14.1799 5.45455 13.0237 5.45455 11.8182V10.9091ZM7.42727 12.7273C7.61509 13.2596 7.9634 13.7205 8.42418 14.0466C8.88496 14.3726 9.43553 14.5477 10 14.5477C10.5645 14.5477 11.115 14.3726 11.5758 14.0466C12.0366 13.7205 12.3849 13.2596 12.5727 12.7273H7.42727Z"
                      fill="#FBFBFB"
                    />
                  </svg>
                </div>
              </div>
              <label
                htmlFor="abaixoDe"
                className="text-sm font-alternative text-gray-700"
              >
                Abaixo de
              </label>
              <input
                type="number"
                placeholder="Valor"
                className="bg-[#D3D3D3] rounded px-4 h-7 w-20 max-w-full"
                {...form.register("max_score")}
              />
            </div>
          </div>
        </div>

        {/* Coluna 2: Práticas */}
        <div className="mb-4 border border-gray-200 rounded-lg p-4">
          <Field label="Práticas">
            <div className="relative mb-2">
              <InputSearch
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Busque por categoria"
              />
            </div>
          </Field>
          <ul className="max-h-64 overflow-y-auto">
            {scenarios?.data
              ?.filter((it) => it.title.includes(search.toLowerCase()))
              .map((it) => {
                const selected = form.watch("test_id") === it.id;
                return (
                  <li
                    key={it.id}
                    data-selected={selected}
                    className="text-sm mb-2 px-4 py-2 bg-[#F4F4F4] rounded-md border-2 border-transparent data-[selected=true]:border-primary-1"
                  >
                    <button
                      type="button"
                      onClick={() =>
                        form.setValue("test_id", selected ? "" : it.id)
                      }
                    >
                      {it.title}
                    </button>
                    <input
                      className="hidden"
                      value={it.id}
                      {...form.register("test_id")}
                    />
                  </li>
                );
              })}
          </ul>
        </div>
      </div>

      {/* Botão Filtrar */}
      <div className="flex justify-center mt-4">
        <Button variant="primary-2" className="h-10 w-32">
          FILTRAR
        </Button>
      </div>
    </form>
  );
}
